import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Play, ArrowLeft, Crown, Clock, Film, Bookmark, User,
  Calendar, Trash2, Loader2, CreditCard, LogOut, Settings
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import VODFooter from '../components/VODFooter';

const API = process.env.REACT_APP_BACKEND_URL || '';

const VODAccount = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'watchlist');
  const [removingId, setRemovingId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('livestreamToken');
    const customerData = localStorage.getItem('livestreamCustomer');
    
    if (!token) {
      navigate('/live/login?from=vod&redirect=/vod/account');
      return;
    }
    
    if (customerData) {
      try {
        setCustomer(JSON.parse(customerData));
      } catch (e) {
        console.error('Error parsing customer data:', e);
      }
    }
    
    fetchAccountData();
  }, [navigate]);

  // Update active tab when URL changes
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const fetchAccountData = async () => {
    try {
      const token = localStorage.getItem('livestreamToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch watchlist, subscription, and purchases in parallel
      const [watchlistRes, libraryRes] = await Promise.all([
        axios.get(`${API}/api/vod/watchlist`, { headers }),
        axios.get(`${API}/api/vod/my-library`, { headers })
      ]);
      
      setWatchlist(watchlistRes.data.videos || []);
      setSubscription(libraryRes.data.subscription);
      setPurchases(libraryRes.data.purchased_videos || []);
    } catch (err) {
      console.error('Error fetching account data:', err);
      if (err.response?.status === 401) {
        navigate('/live/login?from=vod&redirect=/vod/account');
      }
    } finally {
      setLoading(false);
    }
  };

  const removeFromWatchlist = async (videoId) => {
    setRemovingId(videoId);
    try {
      const token = localStorage.getItem('livestreamToken');
      await axios.delete(`${API}/api/vod/watchlist/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWatchlist(watchlist.filter(v => v.id !== videoId));
    } catch (err) {
      console.error('Error removing from watchlist:', err);
    } finally {
      setRemovingId(null);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const hrs = Math.floor(mins / 60);
    if (hrs > 0) return `${hrs}h ${mins % 60}m`;
    return `${mins}m`;
  };

  const handleLogout = () => {
    localStorage.removeItem('livestreamToken');
    localStorage.removeItem('livestreamCustomer');
    navigate('/vod');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-purple-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="vod-account-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/90 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/vod')}
              className="text-gray-400 hover:text-white"
              data-testid="back-button"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <h1 className="text-xl font-bold">My Account</h1>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* User Info Card */}
        {customer && (
          <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-2xl p-6 mb-8 border border-purple-500/20">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-2xl font-bold">
                {customer.username?.charAt(0)?.toUpperCase() || customer.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div>
                <h2 className="text-xl font-semibold">{customer.name || customer.username}</h2>
                {customer.username && (
                  <p className="text-purple-400">@{customer.username}</p>
                )}
                <p className="text-gray-400 text-sm">{customer.email}</p>
              </div>
              {subscription?.status === 'active' && (
                <Badge className="ml-auto bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <Crown className="h-3 w-3 mr-1" />
                  Premium Member
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-white/10 pb-4">
          <Button
            variant={activeTab === 'watchlist' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('watchlist')}
            className={activeTab === 'watchlist' ? 'bg-purple-600 hover:bg-purple-500' : 'text-gray-400 hover:text-white'}
            data-testid="watchlist-tab"
          >
            <Bookmark className="h-4 w-4 mr-2" />
            My Watchlist
            {watchlist.length > 0 && (
              <Badge variant="secondary" className="ml-2 bg-white/10">{watchlist.length}</Badge>
            )}
          </Button>
          <Button
            variant={activeTab === 'purchases' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('purchases')}
            className={activeTab === 'purchases' ? 'bg-purple-600 hover:bg-purple-500' : 'text-gray-400 hover:text-white'}
            data-testid="purchases-tab"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            My Purchases
            {purchases.length > 0 && (
              <Badge variant="secondary" className="ml-2 bg-white/10">{purchases.length}</Badge>
            )}
          </Button>
          <Button
            variant={activeTab === 'subscription' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('subscription')}
            className={activeTab === 'subscription' ? 'bg-purple-600 hover:bg-purple-500' : 'text-gray-400 hover:text-white'}
            data-testid="subscription-tab"
          >
            <Crown className="h-4 w-4 mr-2" />
            Subscription
          </Button>
        </div>

        {/* Watchlist Tab */}
        {activeTab === 'watchlist' && (
          <div data-testid="watchlist-content">
            {watchlist.length === 0 ? (
              <div className="text-center py-16">
                <Bookmark className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Your watchlist is empty</h3>
                <p className="text-gray-400 mb-6">Save videos to watch later by clicking the bookmark icon</p>
                <Button 
                  onClick={() => navigate('/vod/browse')}
                  className="bg-purple-600 hover:bg-purple-500"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Browse Videos
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {watchlist.map((video) => (
                  <div 
                    key={video.id}
                    className="bg-white/5 rounded-xl overflow-hidden border border-white/10 hover:border-purple-500/50 transition-all group"
                    data-testid={`watchlist-item-${video.id}`}
                  >
                    {/* Thumbnail */}
                    <div 
                      className="relative aspect-video cursor-pointer"
                      onClick={() => navigate(`/vod/v/${video.slug || video.id}?autoplay=true`)}
                    >
                      {video.thumbnail_url ? (
                        <img 
                          src={video.thumbnail_url} 
                          alt={video.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-purple-900/50 to-pink-900/50 flex items-center justify-center">
                          <Film className="h-10 w-10 text-white/30" />
                        </div>
                      )}
                      
                      {/* Play overlay */}
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-12 h-12 rounded-full bg-purple-600/90 flex items-center justify-center">
                          <Play className="h-6 w-6 text-white fill-white ml-0.5" />
                        </div>
                      </div>
                      
                      {/* Duration badge */}
                      {video.duration > 0 && (
                        <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/80 text-white text-xs rounded">
                          {formatDuration(video.duration)}
                        </span>
                      )}
                      
                      {/* Premium badge */}
                      {video.tier === 'premium' && (
                        <Badge className="absolute top-2 left-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs">
                          <Crown className="h-3 w-3 mr-1" />
                          Premium
                        </Badge>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="p-4">
                      <h3 
                        className="font-semibold text-white mb-1 line-clamp-2 cursor-pointer hover:text-purple-400 transition-colors"
                        onClick={() => navigate(`/vod/v/${video.slug || video.id}?autoplay=true`)}
                      >
                        {video.title}
                      </h3>
                      
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <Clock className="h-3.5 w-3.5" />
                          {video.added_to_watchlist ? (
                            <span>Added {new Date(video.added_to_watchlist).toLocaleDateString()}</span>
                          ) : (
                            <span>Saved</span>
                          )}
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFromWatchlist(video.id)}
                          disabled={removingId === video.id}
                          className="text-gray-400 hover:text-red-400 hover:bg-red-500/10 -mr-2"
                          data-testid={`remove-watchlist-${video.id}`}
                        >
                          {removingId === video.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Purchases Tab */}
        {activeTab === 'purchases' && (
          <div data-testid="purchases-content">
            {purchases.length === 0 ? (
              <div className="text-center py-16">
                <CreditCard className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No purchases yet</h3>
                <p className="text-gray-400 mb-6">Buy premium videos to access them anytime</p>
                <Button 
                  onClick={() => navigate('/vod/browse')}
                  className="bg-purple-600 hover:bg-purple-500"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Browse Videos
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {purchases.map((video) => (
                  <div 
                    key={video.id}
                    className="bg-white/5 rounded-xl overflow-hidden border border-white/10 hover:border-purple-500/50 transition-all group cursor-pointer"
                    onClick={() => navigate(`/vod/v/${video.slug || video.id}?autoplay=true`)}
                    data-testid={`purchase-item-${video.id}`}
                  >
                    {/* Thumbnail */}
                    <div className="relative aspect-video">
                      {video.thumbnail_url ? (
                        <img 
                          src={video.thumbnail_url} 
                          alt={video.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-purple-900/50 to-pink-900/50 flex items-center justify-center">
                          <Film className="h-10 w-10 text-white/30" />
                        </div>
                      )}
                      
                      {/* Play overlay */}
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-12 h-12 rounded-full bg-purple-600/90 flex items-center justify-center">
                          <Play className="h-6 w-6 text-white fill-white ml-0.5" />
                        </div>
                      </div>
                      
                      {/* Owned badge */}
                      <Badge className="absolute top-2 right-2 bg-green-600 text-white text-xs">
                        Owned
                      </Badge>
                    </div>
                    
                    {/* Info */}
                    <div className="p-4">
                      <h3 className="font-semibold text-white mb-1 line-clamp-2">
                        {video.title}
                      </h3>
                      {video.purchased_at && (
                        <p className="text-sm text-gray-400">
                          Purchased {new Date(video.purchased_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
          <div data-testid="subscription-content">
            {subscription?.status === 'active' ? (
              <div className="max-w-md mx-auto">
                <div className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 rounded-2xl p-6 border border-purple-500/30 text-center">
                  <Crown className="h-12 w-12 text-purple-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">Premium Subscription</h3>
                  <p className="text-gray-400 mb-4">You have full access to all premium content</p>
                  
                  <div className="bg-black/30 rounded-xl p-4 mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-400">Status</span>
                      <Badge className="bg-green-600">Active</Badge>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-400">Plan</span>
                      <span className="text-white font-medium capitalize">{subscription.tier || 'Premium'}</span>
                    </div>
                    {subscription.current_period_end && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Renews</span>
                        <span className="text-white">
                          {new Date(subscription.current_period_end).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <Button
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10"
                    onClick={() => navigate('/vod/membership')}
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Manage Subscription
                  </Button>
                </div>
              </div>
            ) : (
              <div className="max-w-md mx-auto text-center py-8">
                <Crown className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Active Subscription</h3>
                <p className="text-gray-400 mb-6">
                  Subscribe to get unlimited access to all premium content with no ads
                </p>
                <Button 
                  onClick={() => navigate('/vod/membership')}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                >
                  <Crown className="h-4 w-4 mr-2" />
                  Go Premium - £2.99/month
                </Button>
              </div>
            )}
          </div>
        )}
      </main>
      
      {/* Footer */}
      <VODFooter />
    </div>
  );
};

export default VODAccount;
